/**
 * The synthwave loop under the game (the owner, 2026-09-20: "a synth wave beat that just keeps
 * going ... a fun little background beat while you're playing"). It is played, not loaded: every
 * sound is built out of Web Audio oscillators, filters, and one second of noise, so the game keeps
 * its whole soundtrack in a few hundred lines and stays installable offline with nothing to fetch.
 *
 * An eight bar loop in A minor at 104 BPM: four on the floor with a clap on two and four, an
 * eighth note bass on the root of the bar's chord, a detuned saw pad under it, and a sixteenth
 * note arpeggio through a delay over the second half, so the loop breathes instead of repeating
 * flat. The progression is Am F C G, then Am F C E to turn it back around.
 *
 * Two pieces: `Voices` is the instruments and the pattern, on any audio context, and knows nothing
 * about when now is; `Music` is the transport that owns the page's live context and books the
 * pattern ahead of it. Splitting them lets a tool render the same loop into an offline context and
 * write it to a file (`e2e/music-export.ts`), so an exported video carries the game's own sound.
 *
 * Timing comes from the audio clock, never from the frame: the scheduler wakes on a timer, looks a
 * third of a second ahead, and books every note in that window at an exact `AudioContext` time. A
 * dropped frame or a long tick cannot make the beat stumble. Nothing here touches the simulation,
 * so a run sounds different from a replay of it and hashes the same.
 *
 * Browsers refuse to start audio until the player has pressed something, so `resume()` is called
 * from the first key, pad button, or touch and is safe to call again after that.
 */

/** Master gain per level: off, quiet, normal, loud. The settings menu steps through these. */
export const MUSIC_LEVELS = [0, 0.11, 0.22, 0.4] as const;
export const MUSIC_LEVEL_NAMES = ["off", "quiet", "normal", "loud"] as const;

const BPM = 104;
const STEPS_PER_BEAT = 4;
const STEPS_PER_BAR = STEPS_PER_BEAT * 4;
const BARS = 8;
/** Sixteenths in the whole loop, and the seconds one of them lasts. */
export const LOOP_STEPS = STEPS_PER_BAR * BARS;
export const STEP_SECONDS = 60 / BPM / STEPS_PER_BEAT;
/** Seconds of notes booked ahead of the audio clock, and how often the scheduler wakes, ms. */
const LOOKAHEAD_S = 0.3;
const TICK_MS = 45;

/** The chord under each bar: its root as a MIDI note, and the third that makes it minor or major. */
const PROGRESSION: readonly { root: number; third: number }[] = [
  { root: 45, third: 3 }, // Am
  { root: 41, third: 4 }, // F
  { root: 48, third: 4 }, // C
  { root: 43, third: 4 }, // G
  { root: 45, third: 3 }, // Am
  { root: 41, third: 4 }, // F
  { root: 48, third: 4 }, // C
  { root: 40, third: 4 }, // E, the turn back to Am
];

/** Steps of a bar (sixteenths) that carry each drum. */
const KICK_STEPS = [0, 4, 8, 12, 14];
const CLAP_STEPS = [4, 12];
const OPEN_HAT_STEP = 14;
/** The arpeggio's degrees above the chord root, in sixteenths, over the second half of the loop. */
const ARP_DEGREES = [12, 19, 24, 19];

function hz(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

/** True when this page can make sound at all (a test runner or an old browser may not). */
function audioContextClass(): typeof AudioContext | null {
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/**
 * The instruments and the pattern on one audio context: the master chain every voice plays into,
 * the delay the arpeggio sings through, and `book`, which places one sixteenth of the loop at an
 * exact time. It holds no clock of its own, so it works the same on the page's live context and on
 * an offline one rendering the loop to a file.
 */
export class Voices {
  readonly ctx: BaseAudioContext;
  readonly master: GainNode;
  private readonly send: GainNode;
  private readonly noise: AudioBuffer;

  constructor(ctx: BaseAudioContext, gain: number) {
    this.ctx = ctx;
    const master = ctx.createGain();
    master.gain.value = gain;
    // A gentle compressor glues the kick and the bass the way an arcade cabinet's amp would.
    const glue = ctx.createDynamicsCompressor();
    glue.threshold.value = -18;
    glue.ratio.value = 4;
    glue.attack.value = 0.004;
    glue.release.value = 0.18;
    master.connect(glue);
    glue.connect(ctx.destination);
    // The delay the arpeggio sings through: a dotted eighth, darkening as it repeats.
    const send = ctx.createGain();
    send.gain.value = 1;
    const delay = ctx.createDelay(1);
    delay.delayTime.value = (60 / BPM) * 0.75;
    const feedback = ctx.createGain();
    feedback.gain.value = 0.34;
    const damp = ctx.createBiquadFilter();
    damp.type = "lowpass";
    damp.frequency.value = 2200;
    send.connect(delay);
    delay.connect(damp);
    damp.connect(feedback);
    feedback.connect(delay);
    const wet = ctx.createGain();
    wet.gain.value = 0.5;
    damp.connect(wet);
    wet.connect(master);
    const noise = ctx.createBuffer(1, Math.floor(ctx.sampleRate), ctx.sampleRate);
    const data = noise.getChannelData(0);
    let seed = 12345;
    for (let i = 0; i < data.length; i++) {
      seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
      data[i] = (seed / 2147483648 - 1) * 0.6;
    }
    this.master = master;
    this.send = send;
    this.noise = noise;
  }

  /** The one sixteenth at `step` of the loop, booked to sound at `t` on the context's clock. */
  book(step: number, t: number): void {
    const bar = Math.floor(step / STEPS_PER_BAR);
    const inBar = step % STEPS_PER_BAR;
    const chord = PROGRESSION[bar % PROGRESSION.length]!;

    if (KICK_STEPS.includes(inBar)) this.kick(t, inBar === 14 ? 0.55 : 1);
    if (CLAP_STEPS.includes(inBar)) this.clap(t);
    if (inBar % 2 === 0 || inBar === OPEN_HAT_STEP) {
      this.hat(t, inBar === OPEN_HAT_STEP, inBar % 4 === 0 ? 0.5 : 0.32);
    }
    // The bass walks the root in eighths and jumps an octave on the way out of each half bar.
    if (inBar % 2 === 0) {
      const octave = inBar === 6 || inBar === 14 ? 12 : 0;
      this.bass(t, chord.root - 12 + octave, STEP_SECONDS * 1.8);
    }
    // The pad breathes once a bar, three detuned saws an octave over the bass.
    if (inBar === 0) {
      this.pad(t, [chord.root, chord.root + chord.third, chord.root + 7], STEP_SECONDS * STEPS_PER_BAR);
    }
    // The arpeggio joins for the second half of the loop and climbs an octave for the last two bars.
    if (bar >= BARS / 2) {
      const degree = ARP_DEGREES[inBar % ARP_DEGREES.length]!;
      const note = chord.root + degree + (bar >= BARS - 2 ? 12 : 0);
      this.pluck(t, note, inBar % 2 === 0 ? 0.5 : 0.34);
    }
  }

  /** A quick rising arcade zip for a PERFECT. Shares the loop's volume and compressor. */
  perfect(t: number): void {
    const ctx = this.ctx;
    const tone = ctx.createOscillator();
    tone.type = "triangle";
    tone.frequency.setValueAtTime(440, t);
    tone.frequency.exponentialRampToValueAtTime(1320, t + 0.12);
    tone.frequency.exponentialRampToValueAtTime(1760, t + 0.24);
    const envelope = ctx.createGain();
    envelope.gain.setValueAtTime(0, t);
    envelope.gain.linearRampToValueAtTime(0.65, t + 0.008);
    envelope.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
    tone.connect(envelope);
    envelope.connect(this.master);
    tone.onended = () => {
      tone.disconnect();
      envelope.disconnect();
    };
    tone.start(t);
    tone.stop(t + 0.27);

    const air = ctx.createBufferSource();
    air.buffer = this.noise;
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.Q.value = 0.8;
    band.frequency.setValueAtTime(700, t);
    band.frequency.exponentialRampToValueAtTime(4200, t + 0.18);
    const rush = ctx.createGain();
    rush.gain.setValueAtTime(0, t);
    rush.gain.linearRampToValueAtTime(0.5, t + 0.02);
    rush.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    air.connect(band);
    band.connect(rush);
    rush.connect(this.master);
    air.onended = () => {
      air.disconnect();
      band.disconnect();
      rush.disconnect();
    };
    air.start(t);
    air.stop(t + 0.25);
  }

  private kick(t: number, gain: number): void {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(46, t + 0.1);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
    osc.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + 0.34);
  }

  private clap(t: number): void {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const band = ctx.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = 1700;
    band.Q.value = 1.1;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.45, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
    src.connect(band);
    band.connect(g);
    g.connect(this.master);
    src.start(t);
    src.stop(t + 0.2);
  }

  private hat(t: number, open: boolean, gain: number): void {
    const ctx = this.ctx;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 7200;
    const g = ctx.createGain();
    const dur = open ? 0.2 : 0.045;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain * 0.3, t + 0.003);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(hp);
    hp.connect(g);
    g.connect(this.master);
    src.start(t);
    src.stop(t + dur + 0.05);
  }

  private bass(t: number, midi: number, dur: number): void {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = hz(midi);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = 7;
    lp.frequency.setValueAtTime(1500, t);
    lp.frequency.exponentialRampToValueAtTime(420, t + dur * 0.8);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.5, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(lp);
    lp.connect(g);
    g.connect(this.master);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private pad(t: number, midis: number[], dur: number): void {
    const ctx = this.ctx;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(700, t);
    lp.frequency.linearRampToValueAtTime(1600, t + dur * 0.5);
    lp.frequency.linearRampToValueAtTime(900, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(0.1, t + dur * 0.35);
    g.gain.linearRampToValueAtTime(0.0001, t + dur);
    lp.connect(g);
    g.connect(this.master);
    for (const midi of midis) {
      for (const detune of [-7, 7]) {
        const osc = ctx.createOscillator();
        osc.type = "sawtooth";
        osc.frequency.value = hz(midi + 12);
        osc.detune.value = detune;
        osc.connect(lp);
        osc.start(t);
        osc.stop(t + dur + 0.05);
      }
    }
  }

  private pluck(t: number, midi: number, gain: number): void {
    const ctx = this.ctx;
    const osc = ctx.createOscillator();
    osc.type = "square";
    osc.frequency.value = hz(midi);
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(3600, t);
    lp.frequency.exponentialRampToValueAtTime(900, t + 0.18);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain * 0.16, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
    osc.connect(lp);
    lp.connect(g);
    g.connect(this.master);
    g.connect(this.send);
    osc.start(t);
    osc.stop(t + 0.24);
  }
}

export class Music {
  private ctx: AudioContext | null = null;
  private voices: Voices | null = null;
  private timer: number | null = null;
  /** The next sixteenth to book, counted from the start of the loop, and when it falls, s. */
  private step = 0;
  private stepTime = 0;
  private level = 2;
  private wanted = false;
  private visible = !document.hidden;
  private suspending: AudioContext | null = null;
  private lastPerfectTime = -Infinity;
  private pendingResumes = 0;
  private firstResumeAt = 0;

  /** Whether the loop is running and audible. */
  get playing(): boolean {
    return this.shouldPlay && this.ctx?.state === "running" && this.timer !== null;
  }

  get levelIndex(): number {
    return this.level;
  }

  /**
   * Set the volume step (0 is off). Takes effect at once: the loop starts if it was off and the
   * player has already pressed something, and stops when it is turned off.
   */
  setLevel(level: number): void {
    if (!Number.isFinite(level)) return;
    this.level = Math.min(MUSIC_LEVELS.length - 1, Math.max(0, Math.round(level)));
    this.reconcile();
  }

  /**
   * Start (or pick up) the loop. Called from the first press: before one, a browser leaves the
   * audio context suspended and nothing is heard. Safe to call on every press.
   */
  resume(): void {
    this.wanted = true;
    // A broken device can leave resume promises pending forever. Keep retries available to the
    // activation events of a touch, but retire a stuck context on a later gesture rather than
    // collecting another native promise on every press for the rest of the session.
    if (
      this.shouldPlay &&
      this.ctx?.state !== "running" &&
      this.pendingResumes >= 8 &&
      performance.now() - this.firstResumeAt >= 1000
    ) {
      this.clearTimer();
      const old = this.ctx;
      this.ctx = null;
      this.voices = null;
      this.pendingResumes = 0;
      if (old) {
        old.onstatechange = null;
        void old.close().catch(() => {});
      }
    }
    this.reconcile();
  }

  /** Explicitly stop until another press; volume/visibility pauses retain the player's intent. */
  stop(): void {
    this.wanted = false;
    this.reconcile();
  }

  /** Event-driven, never queued while silent. Coalesce simultaneous split-screen/catch-up grades. */
  perfect(): void {
    if (!this.playing || !this.ctx || !this.voices) return;
    const now = this.ctx.currentTime;
    if (now - this.lastPerfectTime < 0.06) return;
    this.lastPerfectTime = now;
    this.voices.perfect(now);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  /** The page went to the background (or came back): browsers throttle the timer, so pause there. */
  setPageVisible(visible: boolean): void {
    this.visible = visible;
    this.reconcile();
  }

  private get shouldPlay(): boolean {
    return this.wanted && this.visible && MUSIC_LEVELS[this.level]! > 0;
  }

  /** Safari's playback session follows media volume, including when its ring switch is silent. */
  private setAudioSession(playing: boolean): void {
    const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
    if (!session) return;
    try {
      const type = playing ? "playback" : "auto";
      if (session.type !== type) session.type = type;
    } catch {
      // Optional policy API: other browsers still use ordinary Web Audio activation.
    }
  }

  private reconcile(): void {
    this.setAudioSession(this.shouldPlay);
    if (this.ctx?.state === "closed") {
      this.ctx.onstatechange = null;
      this.ctx = null;
      this.voices = null;
      this.clearTimer();
    }
    if (this.shouldPlay && !this.ctx && !this.build()) return;
    const ctx = this.ctx;
    if (!ctx || !this.voices) return;
    const gain = this.voices.master.gain;
    gain.cancelScheduledValues(ctx.currentTime);
    if (!this.shouldPlay) {
      // Mute immediately, even if suspend() is delayed or the OS has interrupted the context.
      gain.setValueAtTime(0, ctx.currentTime);
      this.clearTimer();
      this.step = 0;
      this.lastPerfectTime = -Infinity;
      if (ctx.state === "running" && this.suspending !== ctx) {
        this.suspending = ctx;
        void ctx.suspend().then(
          () => {
            if (this.suspending === ctx) this.suspending = null;
            // Volume may have been turned on while suspension was still in flight.
            if (this.ctx === ctx && this.shouldPlay) this.reconcile();
          },
          () => {
            if (this.suspending === ctx) this.suspending = null;
          },
        );
      }
      return;
    }
    gain.setTargetAtTime(MUSIC_LEVELS[this.level]!, ctx.currentTime, 0.02);
    if (ctx.state === "running") {
      this.startTimer();
    } else {
      this.clearTimer();
      // Covers both suspended and Safari's interrupted state. A resume can stay pending during
      // an OS interruption: do not let that promise block a later, trusted touch/key retry.
      if (this.pendingResumes >= 8) return;
      if (this.pendingResumes++ === 0) this.firstResumeAt = performance.now();
      void ctx.resume().then(
        () => {
          if (this.ctx !== ctx) return;
          this.pendingResumes--;
          if (this.shouldPlay && ctx.state === "running") this.startTimer();
          else if (!this.shouldPlay) this.reconcile();
        },
        () => {
          if (this.ctx === ctx) this.pendingResumes--;
        },
      );
    }
  }

  private startTimer(): void {
    if (this.timer !== null || !this.ctx || this.ctx.state !== "running" || !this.shouldPlay) return;
    // Preserve the booked cursor after an interruption: no doubled notes on rapid off/on.
    this.stepTime = Math.max(this.stepTime, this.ctx.currentTime + 0.05);
    this.timer = window.setInterval(() => this.schedule(), TICK_MS);
    this.schedule();
  }

  private build(): boolean {
    const Ctor = audioContextClass();
    if (!Ctor) return false;
    let ctx: AudioContext | null = null;
    try {
      ctx = new Ctor();
      this.voices = new Voices(ctx, MUSIC_LEVELS[this.level]!);
      this.ctx = ctx;
      this.pendingResumes = 0;
      this.step = 0;
      this.stepTime = ctx.currentTime + 0.05;
      this.lastPerfectTime = -Infinity;
      const built = ctx;
      ctx.onstatechange = () => {
        // A closed device is rebuilt on the next press; avoid an automatic reopen loop.
        if (built.state === "closed") this.clearTimer();
        else if (this.ctx === built) this.reconcile();
      };
      return true;
    } catch {
      // No audio on this page (a locked-down browser, a test runner): the game plays silent.
      // A later user gesture can retry a transient device/context construction failure.
      if (ctx) void ctx.close().catch(() => {});
      return false;
    }
  }

  /** Book every sixteenth that falls inside the lookahead window, then wait for the next wake. */
  private schedule(): void {
    const ctx = this.ctx;
    const voices = this.voices;
    if (!ctx || !voices || !this.playing) return;
    const until = ctx.currentTime + LOOKAHEAD_S;
    // A tab that was throttled can leave the cursor far behind: catch it up rather than rushing.
    if (this.stepTime < ctx.currentTime) this.stepTime = ctx.currentTime + 0.05;
    let guard = 0;
    while (this.stepTime < until && guard++ < 128) {
      voices.book(this.step, this.stepTime);
      this.step = (this.step + 1) % LOOP_STEPS;
      this.stepTime += STEP_SECONDS;
    }
  }
}
